import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  selectChatHandoff,
  selectSidebarFocus,
  setSidebarFocus,
  useAppDispatch,
  useAppSelector
} from '@axilon-apps/domains-ontology-ui-state'

import { IngestSessionPane } from '../IngestSession/IngestSessionPane'
import {
  OrchestratorDeleteDialog,
  OrchestratorHistory,
  OrchestratorPane
} from '../Chat/OrchestratorPane'
import type { SidebarTabDescriptor } from '../Chat/TabStrip'
import { useAgentIngestSession } from '../Chat/useAgentIngestSession'
import { useIngestSessionAttention } from '../Chat/useIngestSessionAttention'
import { useOrchestratorChatController } from '../Chat/useOrchestratorChatController'
import { currentIngestTitle } from '../Chat/CurrentIngestEntry'
import { SidebarShell } from './SidebarShell'

// Handoff ids are spaced past any realistic ingest attention count so either
// signal changing the sum re-opens the rail.
const HANDOFF_EXPAND_STRIDE = 1_000_000

const GlobalQuerySidebar = () => {
  const chat = useOrchestratorChatController()
  const {
    handleCloseTab: closeChatTab,
    handleNewTab: startNewChat,
    handleSwitchTab: switchChatTab
  } = chat
  const dispatch = useAppDispatch()
  const focused = useAppSelector(selectSidebarFocus)
  // "Ask in chat" / "Reject" on a work page opens the rail on its new chat.
  const handoffId = useAppSelector(selectChatHandoff)?.id ?? 0
  const {
    session: currentSession,
    isActive: currentSessionActive,
    hasFailed: currentSessionFailed
  } = useAgentIngestSession()
  const { pendingQuestionId, attentionKey } = useIngestSessionAttention(currentSession)
  const lastAttentionKeyRef = useRef(attentionKey)

  const focusOrchestrator = useCallback((): void => {
    dispatch(
      setSidebarFocus(chat.activeTabId === null ? null : { kind: 'chat', id: chat.activeTabId })
    )
  }, [chat.activeTabId, dispatch])

  useEffect(() => {
    if (focused?.kind === 'session' && focused.id !== currentSession?.id) {
      focusOrchestrator()
    }
  }, [currentSession?.id, focusOrchestrator, focused])

  useEffect(() => {
    if (attentionKey === lastAttentionKeyRef.current) return
    lastAttentionKeyRef.current = attentionKey
    if (attentionKey === 0 || !currentSession) return
    dispatch(setSidebarFocus({ kind: 'session', id: currentSession.id }))
  }, [attentionKey, currentSession, dispatch])

  const sessionTabs = useMemo<SidebarTabDescriptor[]>(
    () =>
      currentSession
        ? [
            {
              id: currentSession.id,
              kind: 'session',
              title: currentIngestTitle(currentSession.runName, currentSession.fileName),
              isStreaming: currentSessionActive,
              hasError: currentSessionFailed,
              ...(pendingQuestionId ? { badge: 'question' as const } : {})
            }
          ]
        : [],
    [currentSession, currentSessionActive, currentSessionFailed, pendingQuestionId]
  )
  const tabs = useMemo(
    () => [...sessionTabs, ...chat.openTabSummaries],
    [chat.openTabSummaries, sessionTabs]
  )
  const focusedSessionId =
    focused?.kind === 'session' && focused.id === currentSession?.id ? focused.id : null
  const activeConversation = chat.conversations.find(
    (conversation) => conversation.id === chat.activeTabId
  )
  const shareConversation =
    focusedSessionId === null && activeConversation?.accessScopeId
      ? {
          resourceName: activeConversation.title,
          accessScopeId: activeConversation.accessScopeId
        }
      : undefined

  const handleSwitchTab = useCallback(
    (id: string): void => {
      if (id === currentSession?.id) {
        dispatch(setSidebarFocus({ kind: 'session', id }))
        return
      }
      switchChatTab(id)
      dispatch(setSidebarFocus({ kind: 'chat', id }))
    },
    [currentSession?.id, dispatch, switchChatTab]
  )

  const handleCloseTab = useCallback(
    (id: string): void => {
      if (id === currentSession?.id) return
      closeChatTab(id)
      if (focused?.kind !== 'session') dispatch(setSidebarFocus(null))
    },
    [closeChatTab, currentSession?.id, dispatch, focused?.kind]
  )

  const handleNewTab = useCallback((): void => {
    startNewChat()
    dispatch(setSidebarFocus(null))
  }, [dispatch, startNewChat])

  return (
    <>
      <SidebarShell
        tabs={tabs}
        activeTabId={focusedSessionId ?? chat.activeTabId}
        chatTabId={chat.activeTabId}
        onSwitchTab={handleSwitchTab}
        onCloseTab={handleCloseTab}
        onNewTab={handleNewTab}
        onSelectConversations={focusOrchestrator}
        onExpanded={chat.loadConversations}
        forceExpandKey={attentionKey + handoffId * HANDOFF_EXPAND_STRIDE}
        {...(shareConversation ? { shareConversation } : {})}
        renderPane={(showHistory, closeHistory) =>
          showHistory ? (
            <OrchestratorHistory
              chat={chat}
              onClose={closeHistory}
              onFocusOrchestrator={() => dispatch(setSidebarFocus(null))}
            />
          ) : focusedSessionId !== null ? (
            <IngestSessionPane jobId={focusedSessionId} layout="rail" />
          ) : (
            <OrchestratorPane chat={chat} />
          )
        }
      />
      <OrchestratorDeleteDialog chat={chat} />
    </>
  )
}

export default GlobalQuerySidebar
